
/**
 * Module dependencies.
 */

import { ProductFormat } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import Loading from 'src/components/core/loading';
import React, { ReactElement, useMemo } from 'react';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import split from 'lodash/split';
import styled from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useProductBaseFormats from 'src/api/app/products/use-product-base-formats';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.format;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductFormat[]
};

/**
 * `StyledFilterItem` styled component.
 */

const StyledFilterItem = styled(FilterItem)`
  padding: ${units(3)} 0 ${units(3)} ${units(2)};
`;

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 150px;
`;

/**
 * `FormatFilter` component.
 */

const FormatFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const {
    data: baseFormats,
    isLoading: baseFormatIsLoading,
    isSuccess: baseFormatIsSuccess
  } = useProductBaseFormats<ProductFormat[]>({
    enabled: isEmpty(filtersList)
  });

  const { filters } = useFiltersList();
  const hasFilter = keys(filters).includes(filterName);
  const selectedFilters = hasFilter ? split(filters[filterName], ',').map(formatName => {
    const format = find(baseFormats, ({ name }) => name === formatName);

    return format?.name;
  }) : [];

  const formatsList = useMemo(() => {
    const list = isEmpty(filtersList) ? baseFormats : filtersList;

    return uniqBy(list, 'name');
  }, [baseFormats, filtersList]);

  const isLoading = isEmpty(filtersList) ? baseFormatIsLoading : false;
  const isSuccess = isEmpty(filtersList) ? baseFormatIsSuccess : true;

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.format')}
    >
      {isLoading && (
        <LoadingWrapper>
          <Loading active />
        </LoadingWrapper>
      )}

      {isSuccess && formatsList.map(({ id, name }) => (
        <StyledFilterItem
          key={id}
          label={(
            <FilterItemLabel title={name.replace('.', ',')} />
          )}
          name={filterName}
          value={name}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `FormatFilter` component.
 */

export default FormatFilter;
